import { Badge } from "@/components/ui/Badge";
import { percent } from "../stages";
import type { ColumnsStageData, Pilot } from "../types";

const pilotHeading = (pilot: Pilot) =>
  [
    pilot.version ? `columns v${pilot.version}` : null,
    pilot.papers > 0 ? `${pilot.papers} papers` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const OVERALL_LABEL = "All columns";

// An agent can record one rate for the whole pilot instead of one for each column.
const isOverall = (label: string) => label.trim().toLowerCase() === "all columns";

const columnRates = (pilot: Pilot) => pilot.rates.filter((rate) => !isOverall(rate.label));

/** Schema optimization: the columns, what changed in each pilot, and the rates of each pilot. */
export const ColumnsStage = ({ data }: { data: ColumnsStageData }) => {
  const current = data.columns.map((column) => column.label);
  // A pilot can have rates for a column that a later version removed or renamed.
  const removed = [
    ...new Set(data.pilots.flatMap((pilot) => columnRates(pilot).map((rate) => rate.label))),
  ].filter((label) => !current.includes(label));
  const hasOverall = data.pilots.some((pilot) =>
    pilot.rates.some((rate) => isOverall(rate.label)),
  );
  const labels = [...(hasOverall ? [OVERALL_LABEL] : []), ...current, ...removed];
  const changes = data.pilots.filter((pilot) => pilot.changes);

  if (data.columns.length === 0 && data.pilots.length === 0) {
    return <p className="text-sm text-zinc-600">The agent is drafting the columns.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-zinc-700">
          {data.version ? `Version ${data.version} · ` : ""}
          {data.columns.length} {data.columns.length === 1 ? "column" : "columns"}
          {data.pilots.length > 0 &&
            ` · ${data.pilots.length} ${data.pilots.length === 1 ? "pilot" : "pilots"}`}
        </p>
        {data.locked ? <Badge variant="green">Locked</Badge> : <Badge>Draft</Badge>}
      </div>
      {data.columns.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" aria-label="Columns">
          {data.columns.map((column) => (
            <li
              key={column.label}
              title={column.description || undefined}
              className="rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-xs text-zinc-700"
            >
              <span className="font-medium text-zinc-900">{column.label}</span>
              <span className="text-zinc-500"> · {column.value_type}</span>
              {column.required && <span className="text-zinc-500"> · required</span>}
            </li>
          ))}
        </ul>
      )}
      {changes.length > 0 && (
        <ol className="space-y-1 text-xs text-zinc-700" aria-label="Changes in each pilot">
          {changes.map((pilot) => (
            <li key={pilot.number}>
              <span className="font-medium text-zinc-900">Pilot {pilot.number}:</span>{" "}
              {pilot.changes}
            </li>
          ))}
        </ol>
      )}
      {data.pilots.length > 0 && (
        <div
          role="region"
          aria-label="Rates of each column in each pilot"
          tabIndex={0}
          className="overflow-x-auto rounded-[3px] border border-zinc-200"
        >
          <table className="w-full text-left text-xs">
            <caption className="sr-only">
              The fill rate and the pass rate of each column in each pilot
            </caption>
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  Column
                </th>
                {data.pilots.map((pilot) => (
                  <th key={pilot.number} scope="col" className="px-3 py-2 font-medium">
                    Pilot {pilot.number}
                    {pilotHeading(pilot) && (
                      <span className="font-normal text-zinc-500"> · {pilotHeading(pilot)}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {labels.map((label) => (
                <tr key={label} className="border-t border-zinc-100">
                  <th scope="row" className="px-3 py-2 font-medium text-zinc-900">
                    {label}
                    {removed.includes(label) && (
                      <span className="font-normal text-zinc-500"> · removed or renamed</span>
                    )}
                  </th>
                  {data.pilots.map((pilot) => {
                    const rate =
                      label === OVERALL_LABEL
                        ? pilot.rates.find((item) => isOverall(item.label))
                        : columnRates(pilot).find((item) => item.label === label);
                    return (
                      <td key={pilot.number} className="px-3 py-2 text-zinc-700">
                        {rate
                          ? `fill ${percent(rate.fill_rate, 1)} · pass ${percent(rate.pass_rate, 1)}`
                          : label === OVERALL_LABEL
                            ? "—"
                            : pilot.rates.length === 0
                              ? "No rates recorded"
                              : columnRates(pilot).length === 0
                                ? "Only an overall rate"
                                : "Not tested"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
